/* eslint-disable react/prop-types */
import React, { useRef, useState, useCallback } from "react";
import { Line } from "react-chartjs-2";
import CustomTooltip from "app/common/chart/CustomTooltipComponent";
import { Typography, useMediaQuery, makeStyles } from "@material-ui/core";
import { useSelector } from "react-redux";
import { nFormatter } from "lib/utils";
import { SellOut, SellIn } from "resources/constants";
import moment from "moment";
import _ from "lodash";
import { BsArrowUp } from "react-icons/bs";

const useStyles = makeStyles(() => ({
  arrowPercentage: {
    color: "#939393",
    marginRight: "1px",
  },
  containerRow: {
    display: "flex",
    alignItems: "center",
    fontSize: "12px",
  },
}));

function VolumeChartComponent({ data, historicsDataYoY }) {
  const refs = useRef();
  const classes = useStyles();
  const [tooltipProps, setTooltipProps] = useState({});
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const configCatSlice = useSelector((state) => state.auth.configCatUser);
  const sellOutLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? SellOut
    : "Sell Out";
  const sellInLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? SellIn
    : "Sell In";

  const setPositionAndData = (payload) => {
    setTooltipProps({
      top: payload.top,
      left: payload.left,
      position: "fixed",
      tooltipModel: payload.tooltipModel,
      showTooltip: true,
    });
  };

  const hide = () => {
    setTooltipProps({
      ...tooltipProps,
      showTooltip: false,
    });
  };

  const getHistoricValue = (
    historicData,
    historicDate,
    valueNumber,
    typeDate,
    label,
    typeDateDiff = ""
  ) => {
    const formatDate = moment(historicDate, "MMM'YY")
      .add(14, "days")
      .format("YYYY-MM-DD");
    const historicValue = _.find(historicData, (item) => {
      return typeDate === "years" || typeDateDiff === "years"
        ? moment(item.date).format("MMM'YY") ===
            moment(formatDate)
              .subtract(valueNumber, typeDate)
              .format("MMM'YY")
        : item.x ===
            moment(formatDate)
              .subtract(valueNumber, typeDate)
              .format("MMM'YY");
    });

    const isSellIn = label === "Sell In" ? "sum_units_in" : undefined;
    const specificvalue = label === "Sell out" ? "sum_units" : isSellIn;
    return typeDate === "years" || typeDateDiff === "years"
      ? historicValue && historicValue[specificvalue]
      : historicValue?.y;
  };

  const getHistoricValueForYoY = (historicDate, label) => {
    return getHistoricValue(historicsDataYoY, historicDate, 1, "years", label);
  };

  const getHistoricValueForMoM = (historicDate, historicsDataMoM, label) => {
    let previousMonthHistoricValue = getHistoricValue(
      historicsDataMoM,
      historicDate,
      1,
      "months",
      label
    );

    if (!previousMonthHistoricValue) {
      previousMonthHistoricValue = getHistoricValue(
        historicsDataYoY,
        historicDate,
        1,
        "months",
        label,
        "years"
      );
    }

    return previousMonthHistoricValue;
  };

  const getDifferenceYoyOrMom = (value, historicValue) => {
    return _.round((100 * (value - historicValue)) / historicValue, 1);
  };

  const maxNumStores = Math.max(...data?.datasets[2].data.map((el) => el.y));
  const minNumStores = Math.min(...data?.datasets[2].data.map((el) => el.y));
  const difNumStores = maxNumStores - minNumStores;

  const chartOptions = {
    scales: {
      xAxes: [
        {
          id: "Time",
          offset: true,
          gridLines: {
            drawOnChartArea: false,
            drawTicks: false,
          },
          ticks: {
            padding: 10,
            beginAtZero: false,
            autoSkip: true,
            maxTicksLimit: 6,
            maxRotation: isSmallScreen ? 45 : 0,
          },
        },
      ],
      yAxes: [
        {
          id: "units",
          scaleLabel: {
            labelString: `Units`,
            display: false,
          },
          gridLines: {
            drawOnChartArea: true,
            drawTicks: false,
          },
          ticks: {
            padding: 10,
            beginAtZero: false,
            autoSkip: true,
            maxTicksLimit: 6,
            maxRotation: 0,
            minRotation: 0,
            callback: React.useCallback((value) => {
              return `${nFormatter(value)}`;
            }, []),
          },
          position: "left",
        },
        {
          id: "distinct_count_point_of_sale_id",
          display: false,
          scaleLabel: {
            labelString: `N. Stores (Distrib.)`,
            display: false,
          },
          gridLines: {
            drawOnChartArea: true,
            drawTicks: false,
          },
          ticks: {
            padding: 10,
            beginAtZero: false,
            autoSkip: true,
            maxTicksLimit: 6,
            maxRotation: 0,
            minRotation: 0,
            max: data ? maxNumStores + difNumStores : undefined,
            min: data
              ? minNumStores -
                (minNumStores - difNumStores > 0 ? difNumStores : 0)
              : undefined,
          },
          position: "right",
        },
      ],
    },
    layout: {
      padding: {
        left: 10,
        right: 15,
        top: 15,
        bottom: 15,
      },
    },
    legend: {
      display: false,
    },
    tooltips: {
      enabled: false,
      intersect: isSmallScreen,
      mode: "index",
      axis: "x",
      custom: useCallback((tooltipModel) => {
        if (tooltipModel.opacity === 0) {
          hide();
          return;
        }

        const chart = refs.current.chartInstance;
        const { canvas } = chart.ctx;

        if (canvas) {
          const position = canvas.getBoundingClientRect();

          // set position of tooltip
          const left =
            tooltipModel.caretX <= chart.width / 2
              ? position.left + tooltipModel.caretX + 40
              : position.left + tooltipModel.caretX - 220;
          const top =
            tooltipModel.caretY <= chart.height / 2
              ? position.top + tooltipModel.caretY - 20
              : position.top + tooltipModel.caretY - 140;

          setPositionAndData({ top, left, tooltipModel });
        }
      }, []),
    },
    responsive: true,
    maintainAspectRatio: false,
  };
  return (
    <>
      {tooltipProps.showTooltip ? (
        <CustomTooltip
          tooltipProps={tooltipProps}
          items={_.map(tooltipProps.tooltipModel.dataPoints, (dataPoint) => {
            return {
              content: (
                <div>
                  <Typography variant="body2">
                    {refs.current.chartInstance.data.datasets[
                      dataPoint.datasetIndex
                    ].label === "Sell out"
                      ? sellOutLabelByTenant
                      : ""}
                    {refs.current.chartInstance.data.datasets[
                      dataPoint.datasetIndex
                    ].label === "Sell In"
                      ? sellInLabelByTenant
                      : ""}
                    {refs.current.chartInstance.data.datasets[
                      dataPoint.datasetIndex
                    ].label === "Sell In" ||
                    refs.current.chartInstance.data.datasets[
                      dataPoint.datasetIndex
                    ].label === "Sell out"
                      ? ""
                      : refs.current.chartInstance.data.datasets[
                          dataPoint.datasetIndex
                        ].label}
                  </Typography>
                  <Typography variant="caption">
                    {dataPoint.value
                      ? `${nFormatter(parseFloat(dataPoint.value))} ${refs
                          .current.chartInstance.data.datasets[
                          dataPoint.datasetIndex
                        ].unit || ""}`
                      : ""}
                  </Typography>
                  {getHistoricValueForYoY(
                    dataPoint.label,
                    refs.current.chartInstance.data.datasets[
                      dataPoint.datasetIndex
                    ].label
                  ) &&
                    (refs.current.chartInstance.data.datasets[
                      dataPoint.datasetIndex
                    ].label === "Sell out" ||
                      refs.current.chartInstance.data.datasets[
                        dataPoint.datasetIndex
                      ].label === "Sell In") &&
                    getDifferenceYoyOrMom(
                      dataPoint.value,
                      getHistoricValueForYoY(
                        dataPoint.label,
                        refs.current.chartInstance.data.datasets[
                          dataPoint.datasetIndex
                        ].label
                      )
                    ) !== 0 && (
                      <div className={`${classes.containerRow}`}>
                        <BsArrowUp
                          size={16}
                          strokeWidth={1.5}
                          style={{
                            marginRight: "5px",
                            color: `${
                              getDifferenceYoyOrMom(
                                dataPoint.value,
                                getHistoricValueForYoY(
                                  dataPoint.label,
                                  refs.current.chartInstance.data.datasets[
                                    dataPoint.datasetIndex
                                  ].label
                                )
                              ) > 0
                                ? "#5AB793"
                                : "#FD6464"
                            }`,
                            transform: `rotateX(${
                              getDifferenceYoyOrMom(
                                dataPoint.value,
                                getHistoricValueForYoY(
                                  dataPoint.label,
                                  refs.current.chartInstance.data.datasets[
                                    dataPoint.datasetIndex
                                  ].label
                                )
                              ) > 0
                                ? "0deg"
                                : "180deg"
                            })`,
                          }}
                        />
                        {`${Math.abs(
                          getDifferenceYoyOrMom(
                            dataPoint.value,
                            getHistoricValueForYoY(
                              dataPoint.label,
                              refs.current.chartInstance.data.datasets[
                                dataPoint.datasetIndex
                              ].label
                            )
                          )
                        )}% YoY`}
                      </div>
                    )}
                  {getHistoricValueForMoM(
                    dataPoint.label,
                    refs.current.chartInstance.data.datasets[
                      dataPoint.datasetIndex
                    ].data,
                    refs.current.chartInstance.data.datasets[
                      dataPoint.datasetIndex
                    ].label
                  ) &&
                    (refs.current.chartInstance.data.datasets[
                      dataPoint.datasetIndex
                    ].label === "Sell out" ||
                      refs.current.chartInstance.data.datasets[
                        dataPoint.datasetIndex
                      ].label === "Sell In") &&
                    getDifferenceYoyOrMom(
                      dataPoint.value,
                      getHistoricValueForMoM(
                        dataPoint.label,
                        refs.current.chartInstance.data.datasets[
                          dataPoint.datasetIndex
                        ].data,
                        refs.current.chartInstance.data.datasets[
                          dataPoint.datasetIndex
                        ].label
                      )
                    ) !== 0 && (
                      <div className={`${classes.containerRow}`}>
                        <BsArrowUp
                          size={16}
                          strokeWidth={1.5}
                          style={{
                            marginRight: "5px",
                            color: `${
                              getDifferenceYoyOrMom(
                                dataPoint.value,
                                getHistoricValueForMoM(
                                  dataPoint.label,
                                  refs.current.chartInstance.data.datasets[
                                    dataPoint.datasetIndex
                                  ].data,
                                  refs.current.chartInstance.data.datasets[
                                    dataPoint.datasetIndex
                                  ].label
                                )
                              ) > 0
                                ? "#5AB793"
                                : "#FD6464"
                            }`,
                            transform: `rotateX(${
                              getDifferenceYoyOrMom(
                                dataPoint.value,
                                getHistoricValueForMoM(
                                  dataPoint.label,
                                  refs.current.chartInstance.data.datasets[
                                    dataPoint.datasetIndex
                                  ].data,
                                  refs.current.chartInstance.data.datasets[
                                    dataPoint.datasetIndex
                                  ].label
                                )
                              ) > 0
                                ? "0deg"
                                : "180deg"
                            })`,
                          }}
                        />
                        {`${Math.abs(
                          getDifferenceYoyOrMom(
                            dataPoint.value,
                            getHistoricValueForMoM(
                              dataPoint.label,
                              refs.current.chartInstance.data.datasets[
                                dataPoint.datasetIndex
                              ].data,
                              refs.current.chartInstance.data.datasets[
                                dataPoint.datasetIndex
                              ].label
                            )
                          )
                        )}% MoM`}
                      </div>
                    )}
                </div>
              ),
              borderStyle: {
                border: refs.current.chartInstance.data.datasets[
                  dataPoint.datasetIndex
                ]?.borderDash
                  ? `dashed 2px ${
                      refs.current.chartInstance.data.datasets[
                        dataPoint.datasetIndex
                      ].hoverBorderColor
                    }`
                  : `2px solid ${
                      refs.current.chartInstance.data.datasets[
                        dataPoint.datasetIndex
                      ].borderColor
                    }`,
              },
            };
          })}
          tooltipData={{
            title: `Volume - ${
              refs.current.chartInstance.data.datasets[
                tooltipProps.tooltipModel.dataPoints[0].datasetIndex
              ].data[tooltipProps.tooltipModel.dataPoints[0].index].x
            }`,
          }}
        />
      ) : null}
      <Line ref={refs} data={data} options={chartOptions} />
    </>
  );
}

export default VolumeChartComponent;
